import styled from '@emotion/styled';
import Button from '@base/Button/Button';
import Dialog from '@comp/Dialog/Dialog';
import Cropper from 'react-cropper';
import RotateIcon from '@icon/Rotate';
import ImageCrossLMP from '@icon/ImageCrossLMP';

import H5 from '@base/H5/H5';
import { mediaQuery, Spacings } from '@styles/index';
import P1 from '@base/P1/P1';
import useWindowSize from '@hooks/useWindowSize';
import { DoubtsImageUploadError } from '@utils/customErrors';
import { captureException } from '@utils/sentry';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import Image from '@base/Image';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';
import { fetchS3SignInUrl } from '@api/fetchers/others';
import defaultExtraProps from '../utils/defaultExecptionValues';

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
`;

const AnimationFallBackImage = ({ imageURL }) => {
  return <Image src={imageURL} width={80} height={80} alt="doubt-state-card" />;
};

export const getValidImageName = (fileName, defaultName = 'default.jpg') =>
  fileName?.match(/.(jpg|jpeg|png)$/i) ? fileName : defaultName;

const LeftDiv = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const RightDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ isUploading }) => (!isUploading ? 'pointer' : 'not-allowed')};
`;

const StyH5 = styled(H5)`
  margin-bottom: ${Spacings.SPACING_1B};
`;

const RotateText = styled(P1)`
  margin-top: ${Spacings.SPACING_1B};
`;

const RotateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: ${({ isUploading }) => (!isUploading ? 'pointer' : 'not-allowed')};
  align-items: center;
`;

const DialogWrapper = styled.div`
  grid-column: span 10;
  display: flex;
  flex-direction: column;
  background: var(--color-base-1);
  width: 552px;
  height: 696px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 508px;
  }
`;
const HeadingDiv = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_6B};
  height: 72px;
  justify-content: space-between;
`;

const ImageCropper = styled.div`
  background: var(--color-base-0);
  & .cropper-point {
    background: none;
  }
  & .cropper-line {
    background: white;
  }
  & .cropper-view-box {
    border: ${Spacings.SPACING_1} solid white;
    outline: none;
  }
  & .cropper-dashed {
    border: none;
  }
  & .cropper-center {
    opacity: 0;
  }
  & .point-e {
    opacity: 0;
  }
  & .point-n {
    opacity: 0;
  }
  & .point-w {
    opacity: 0;
  }
  & .point-s {
    opacity: 0;
  }
  & .point-se {
    height: ${Spacings.SPACING_6B};
    width: ${Spacings.SPACING_6B};
    border-bottom: ${Spacings.SPACING_3} solid white;
    border-right: ${Spacings.SPACING_3} solid white;
    opacity: 1;
  }
  & .point-sw {
    height: ${Spacings.SPACING_6B};
    width: ${Spacings.SPACING_6B};
    border-left: ${Spacings.SPACING_3} solid white;
    border-bottom: ${Spacings.SPACING_3} solid white;
    opacity: 1;
  }
  & .point-nw {
    height: ${Spacings.SPACING_6B};
    width: ${Spacings.SPACING_6B};
    border-top: ${Spacings.SPACING_3} solid white;
    border-left: ${Spacings.SPACING_3} solid white;
    opacity: 1;
  }
  & .point-ne {
    height: ${Spacings.SPACING_6B};
    width: ${Spacings.SPACING_6B};
    border-top: ${Spacings.SPACING_3} solid white;
    border-right: ${Spacings.SPACING_3} solid white;
    opacity: 1;
  }
`;

const DialogLower = styled.div`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_6B};
  height: 72px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  bottom: 0px;
`;

const DialogLowerRightDiv = styled.div``;

const StylButton = styled(Button)`
  color: var(--color-text-tertiary);
`;

const ImageCropperDailog = ({
  isCropDialogOpen,
  fileObj,
  isUploading,
  setCropDialogOpen,
  setCropper,
  setIsUploading,
  cropper,
  setError,
  setUploadedImageUrl,
  cropImgSrc,
  setSignedUrl
}) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const { isMobile } = useWindowSize();
  const myUserUID = useMyInfo(getMeUid);

  const getCropData = async () => {
    try {
      if (typeof cropper !== 'undefined') {
        const croppedImageData = cropper.getCroppedCanvas().toDataURL();
        setIsUploading(true);
        const fileName = getValidImageName(fileObj.name);
        let fileExtension = `image/${fileName
          .split('.')
          .slice(-1)[0]
          .toLowerCase()}`;

        if (fileExtension.includes('jpg')) {
          fileExtension = 'image/jpeg';
        }

        const signedUrlRes = await fetchS3SignInUrl(
          fileName,
          3,
          '',
          fileExtension
        );
        if (signedUrlRes.error) {
          const defaultExtraPropsObj = defaultExtraProps(
            myUserUID,
            signedUrlRes
          );
          const extraProps = {
            type: 'tag',
            key: 'statusCode',
            value: signedUrlRes?.statusCode
          };
          const combinedProps = [...defaultExtraPropsObj, extraProps];
          captureException(
            new DoubtsImageUploadError('', signedUrlRes),
            null,
            null,
            combinedProps
          );
        }
        const { signed_url, public_url } = signedUrlRes;
        setSignedUrl(signedUrlRes);

        if (!signed_url) return;
        // eslint-disable-next-line new-cap
        const base64Data = new Buffer.from(
          croppedImageData.replace(/^data:image\/\w+;base64,/, ''),
          'base64'
        );
        const res = await fetch(signed_url, {
          method: 'PUT',
          body: base64Data,
          headers: {
            'content-type': fileExtension,
            'content-encoding': 'base64',
            'content-length': croppedImageData.length
          }
        });
        if (res.ok) {
          setUploadedImageUrl(public_url);
          setError(null);
          setIsUploading(false);
          setCropDialogOpen(false);
        } else {
          setIsUploading(false);
          setCropDialogOpen(false);
          setError('Image upload failed, please try again');
          const defaultExtraPropsObj = defaultExtraProps(myUserUID, res);
          captureException(
            new DoubtsImageUploadError('Image upload failed', res),
            null,
            null,
            defaultExtraPropsObj
          );
        }
      }
    } catch (error) {
      const defaultExtraPropsObj = defaultExtraProps(myUserUID);
      captureException(
        new DoubtsImageUploadError(error?.message),
        null,
        null,
        defaultExtraPropsObj
      );
    }
  };
  if (cropper) {
    const data = cropper.getCanvasData();
    const { left, top, height, width } = data;
    cropper.setCropBoxData({ left, top, width, height });
  }
  const handleRotate = () => {
    if (isUploading) return;
    cropper.rotate(90);
    const idata = cropper.getCanvasData();
    // eslint-disable-next-line no-shadow
    const { left, top, height, width } = idata;
    cropper.setCropBoxData({ left, top, width, height });
  };
  const loadingStateAnimation = (
    <AnimationContainer
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      Fallback={<AnimationFallBackImage imageURL="doubt20/expandIconDM.svg" />}
      src={getAnimationPath(
        isDarkModeOn
          ? 'doubtsOnWeb/3-dot-loader-Dark-mode.json'
          : 'doubtsOnWeb/3-dot-loader-Light-mode.json'
      )}
    />
  );

  const onCloseDialog = () => {
    if (isUploading) return;
    setCropDialogOpen(false);
  };
  return (
    <Dialog
      dialogState={isCropDialogOpen}
      acceptOnClick={() => {}}
      acceptLabelVariant="hollow"
      showButtons
      center
      onClose={onCloseDialog}
    >
      {cropImgSrc && (
        <DialogWrapper>
          <HeadingDiv>
            <LeftDiv>
              <StyH5>Crop one question only</StyH5>
            </LeftDiv>
            <RightDiv onClick={onCloseDialog}>
              <ImageCrossLMP size="16px" />
            </RightDiv>
          </HeadingDiv>
          <ImageCropper>
            <Cropper
              style={{
                height: isMobile ? '364px' : '552px',
                width: isMobile ? '100%' : '552px',
                objectFit: 'contain'
              }}
              src={cropImgSrc}
              onInitialized={(instance) => {
                setCropper(instance);
              }}
              zoomable={false}
              background={false}
            />
          </ImageCropper>
          <DialogLower>
            <RotateWrapper onClick={handleRotate} isUploading={isUploading}>
              <RotateIcon size="14px" color="var(--color-text-primary)" />
              <RotateText>Rotate</RotateText>
            </RotateWrapper>

            <DialogLowerRightDiv>
              <StylButton
                variant={isUploading ? 'loading' : 'filled'}
                color="black"
                label={isUploading ? loadingStateAnimation : 'Next'}
                width="100px"
                maxHeight="sm"
                fontSize="14px"
                onClick={getCropData}
              />
            </DialogLowerRightDiv>
          </DialogLower>
        </DialogWrapper>
      )}
    </Dialog>
  );
};

export default ImageCropperDailog;
